import { useCallback, useEffect, useRef } from 'react';

/**
 * Хук реализующий вспомогательные функции для работы с формой
 * @param selector Селектор формы ( по умолчанию возмется первая попавшиеся )
 * @returns Кортеж с вспомогательными функциями
 */
const useFormControls = (selector?: string) => {
	// Ссылка на форму
	const formRef = useRef<HTMLFormElement>();

	/** Ищем и запоминаем форму */
	useEffect(() => {
		if (!selector) {
			if (!!document.forms && document.forms.length > 0) {
				formRef.current = document.forms[0];
			} else {
				throw new Error('Форма не найдена!');
			}
		} else {
			const form = document.querySelector(selector);
			if (form) {
				formRef.current = form as HTMLFormElement;
			} else {
				throw new Error('Форма не найдена!');
			}
		}
	}, [selector]);

	/** Устанавливает фокус на поле ввода формы по указанному индексу ( по умолчанию - 0 ) */
	const setFocus = useCallback((index = 0) => {
		const formInputs = _getInputs();
		_setFocus(formInputs, index);
	}, []);

	/** Устанавливает фокус на следующее, после указанного элемента, поле ввода формы */
	const nextFocus = useCallback((elementOrNameElement: Element | string) => {
		const formInputs = _getInputs();
		const startIndex =
			typeof elementOrNameElement !== 'string'
				? _getIndexFromSet(formInputs, elementOrNameElement)
				: _findElementIndex(elementOrNameElement, formInputs);
		const nextIndex = _getNextVisibleInputIndex(startIndex, formInputs);
		setFocus(nextIndex);
	}, []);

	/** Устанавливает фокус на предыдущее, после указанного элемента, поле ввода формы */
	const previousFocus = useCallback((element: Element) => {
		const formInputs = _getInputs();
		const startIndex = _getIndexFromSet(formInputs, element);
		const previousIndex = _getPreviousVisibleInputIndex(
			startIndex,
			formInputs,
		);
		if (previousIndex >= 0 || previousIndex <= formInputs.length) {
			setFocus(previousIndex);
		}
	}, []);

	/** Возвращает индекс поля ввода по его наименованию */
	const _findElementIndex = (name: string, formInputs: Element[]) => {
		for (let index = 0; index < formInputs.length; index++) {
			const input = formInputs[index];
			if (input.getAttribute('name') === name) {
				return index;
			}
		}
		return -1;
	};

	/** Возвращает индекс видимого следующего инпута  */
	const _getNextVisibleInputIndex = (
		startIndex: number,
		formInputs: Element[],
	) => {
		for (let i = startIndex + 1; i < formInputs.length; i++) {
			const element = formInputs[i];
			if (element && !element.parentElement?.hidden) {
				return i;
			}
		}
		return -1;
	};

	/** Возвращает индекс видимого предидущего инпута */
	const _getPreviousVisibleInputIndex = (
		startIndex: number,
		formInputs: Element[],
	) => {
		for (let i = startIndex + 1; i < formInputs.length; i++) {
			const element = formInputs[i];
			if (element && !element.parentElement?.hidden) {
				return i;
			}
		}
		return -1;
	};

	/** Устаналивает фокус на поле ввода с указанным индексом */
	const _setFocus = (formInputs: Element[], index = 0) => {
		if (!formInputs.length || index >= formInputs.length) {
			console.warn(formInputs, index);
			throw new Error(
				`На форме не найдено поле ввода с указанным ( ${index} ) индексом!`,
			);
		}
		const input = formInputs[index] as HTMLInputElement;
		if (input) {
			input.focus();
		}
	};

	/** Возвращает поля ввода ДФ */
	const _getInputs = () => {
		if (!formRef.current) {
			return [];
		}
		return Array.from(formRef.current);
	};

	/** Возвращает индекс элемента */
	const _getIndexFromSet = (set: Element[], elm: Element): number => {
		for (const i in set) {
			if (set[i] === elm) {
				return parseInt(i);
			}
		}
		return -1;
	};

	return {
		/** Ссылка на форму */
		ref: formRef.current,
		/** Устанавливает фокус на поле ввода формы по указанному индексу ( по умолчанию - 0 ) */
		setFocus,
		/** Устанавливает фокус на следующее, после указанного элемента, поле ввода формы */
		nextFocus,
		/** Устанавливает фокус на предыдущее, после указанного элемента, поле ввода формы */
		previousFocus,
	} as const;
};

export default useFormControls;
